import React from 'react';

const DeploymentArchitectureDiagram: React.FC = () => {
  return (
    <div className="p-6 bg-white rounded-lg shadow-lg transition-all duration-300">
      <h2 className="mb-6 text-2xl font-bold text-center text-blue-800">应用部署架构图</h2>
      <p className="mb-4 text-gray-600 text-center max-w-4xl mx-auto">
        应用部署架构采用容器化集群模式，保证系统高可用性和扩展性
      </p>
      
      <div className="relative w-full h-[500px] bg-gradient-to-b from-gray-50 to-gray-100 rounded-lg p-6 border-2 border-dashed border-gray-300">
        <div className="space-y-6">
          {/* 负载均衡层 */}
          <div className="bg-purple-100 rounded-lg p-4 border-2 border-purple-300 shadow-md max-w-3xl mx-auto">
            <h3 className="text-center font-medium text-purple-800 mb-3">负载均衡集群</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded p-3 text-sm text-center border border-purple-200">
                <div className="font-medium">主负载均衡器</div>
                <div className="text-xs text-gray-500 mt-1">Nginx + Keepalived</div>
              </div>
              <div className="bg-white rounded p-3 text-sm text-center border border-purple-200">
                <div className="font-medium">备负载均衡器</div>
                <div className="text-xs text-gray-500 mt-1">故障自动切换</div>
              </div>
            </div>
          </div>

          {/* 应用服务器集群 */}
          <div className="flex justify-between gap-6">
            {/* Fusion集群 */}
            <div className="flex-1 bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border-2 border-red-300 shadow-md">
              <h3 className="text-center font-medium text-red-800 mb-3">Fusion集群</h3>
              <div className="space-y-3">
                <div className="bg-white rounded p-3 text-sm border border-red-200">
                  <div className="font-medium mb-2">应用服务 (Docker容器)</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-red-50 p-1 rounded">交易服务</div>
                    <div className="bg-red-50 p-1 rounded">风控服务</div>
                    <div className="bg-red-50 p-1 rounded">资金服务</div>
                    <div className="bg-red-50 p-1 rounded">物流服务</div>
                  </div>
                </div>
                <div className="bg-white rounded p-3 text-sm border border-red-200">
                  <div className="font-medium mb-2">中间件服务</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-red-50 p-1 rounded">Redis集群</div>
                    <div className="bg-red-50 p-1 rounded">消息队列</div>
                  </div>
                </div>
              </div>
            </div>

            {/* X-Ray集群 */}
            <div className="flex-1 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border-2 border-blue-300 shadow-md">
              <h3 className="text-center font-medium text-blue-800 mb-3">X-Ray集群</h3>
              <div className="space-y-3">
                <div className="bg-white rounded p-3 text-sm border border-blue-200">
                  <div className="font-medium mb-2">应用服务 (Docker容器)</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-blue-50 p-1 rounded">数据采集</div>
                    <div className="bg-blue-50 p-1 rounded">实时分析</div>
                    <div className="bg-blue-50 p-1 rounded">风险预警</div>
                    <div className="bg-blue-50 p-1 rounded">策略引擎</div>
                  </div>
                </div>
                <div className="bg-white rounded p-3 text-sm border border-blue-200">
                  <div className="font-medium mb-2">数据服务</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-blue-50 p-1 rounded">时序数据库</div>
                    <div className="bg-blue-50 p-1 rounded">数据缓存</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 存储层 */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border-2 border-green-300 shadow-md max-w-4xl mx-auto">
            <h3 className="text-center font-medium text-green-800 mb-3">持久化存储层</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded p-3 text-sm text-center border border-green-200">
                <div className="font-medium">MySQL主库</div>
                <div className="text-xs text-gray-500 mt-1">交易数据</div>
              </div>
              <div className="bg-white rounded p-3 text-sm text-center border border-green-200">
                <div className="font-medium">MySQL从库</div>
                <div className="text-xs text-gray-500 mt-1">读写分离</div>
              </div>
              <div className="bg-white rounded p-3 text-sm text-center border border-green-200">
                <div className="font-medium">ClickHouse</div>
                <div className="text-xs text-gray-500 mt-1">分析数据</div>
              </div>
              <div className="bg-white rounded p-3 text-sm text-center border border-green-200">
                <div className="font-medium">MinIO</div>
                <div className="text-xs text-gray-500 mt-1">对象存储</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg shadow-sm">
        <h3 className="font-semibold text-blue-800 mb-2">部署架构说明</h3>
        <p className="text-sm text-gray-700">
          整体系统采用容器化部署方案，使用Docker容器和Kubernetes进行编排管理。负载均衡层采用Nginx+Keepalived实现高可用，
          应用服务采用微服务架构并水平扩展。Fusion和X-Ray各自独立部署以保证系统隔离性，通过消息队列和API进行通信。
          存储层采用主从架构和分布式设计，确保数据的高可用性和可扩展性。
        </p>
      </div>
    </div>
  );
};

export default DeploymentArchitectureDiagram; 